import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DataTable from 'react-data-table-component';
import { Button, Modal, Form, Badge, Nav, Tab } from 'react-bootstrap';
import Swal from 'sweetalert2';
import DashboardLayout from '../../components/DashboardLayout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const MONTH_NAMES = [
  'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
  'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
];

const getCurrentMonthValue = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${now.getFullYear()}-${month}`;
};

const TaskManagement = () => {
  const [tasks, setTasks] = useState([]);
  const [officers, setOfficers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPic, setFilterPic] = useState('');
  const [activeTab, setActiveTab] = useState('tasks');
  const [leaderboardMonth, setLeaderboardMonth] = useState(getCurrentMonthValue());
  const [leaderboard, setLeaderboard] = useState([]);
  const [topPerformer, setTopPerformer] = useState(null);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [overviewFilter, setOverviewFilter] = useState('active');
  
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    points: 0,
    deadline: '',
    pic_id: ''
  });

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(res.data);
    } catch (err) {
      console.error('Failed to fetch tasks', err);
    }
  };

  const fetchOfficers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/officers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOfficers(res.data);
    } catch (err) {
      console.error('Failed to fetch officers', err);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      setLeaderboardLoading(true);
      const token = localStorage.getItem('token');
      const [year, month] = leaderboardMonth.split('-');
      const res = await axios.get(`${API_URL}/api/tasks/leaderboard`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { month, year }
      });
      setLeaderboard(res.data.leaderboard || []);
      setTopPerformer(res.data.top_performer || null);
    } catch (err) {
      console.error('Failed to fetch leaderboard', err);
    } finally {
      setLeaderboardLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchOfficers();
  }, []);

  useEffect(() => {
    if (activeTab === 'leaderboard') {
      fetchLeaderboard();
    }
  }, [activeTab, leaderboardMonth]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (editTask) {
        await axios.put(`${API_URL}/api/tasks/${editTask.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        Swal.fire('Berhasil', 'Task berhasil diupdate', 'success');
      } else {
        await axios.post(`${API_URL}/api/tasks`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        Swal.fire('Berhasil', 'Task berhasil ditambahkan', 'success');
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.error || 'Terjadi kesalahan', 'error');
    }
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: 'Hapus task?',
      text: "Task yang dihapus tidak bisa dikembalikan!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ya, hapus!'
    });
    
    if (confirm.isConfirmed) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API_URL}/api/tasks/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        Swal.fire('Deleted!', 'Task telah dihapus.', 'success');
        fetchData();
      } catch (err) {
        Swal.fire('Error', 'Gagal menghapus task', 'error');
      }
    }
  };

  const handleApprove = async (id) => {
    const confirm = await Swal.fire({
      title: 'Approve Task?',
      text: "Anda akan menyetujui klaim selesainya task ini.",
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Ya, Approve'
    });

    if (confirm.isConfirmed) {
      try {
        const token = localStorage.getItem('token');
        await axios.put(`${API_URL}/api/tasks/${id}/approve`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        Swal.fire('Approved!', 'Task berhasil disetujui.', 'success');
        fetchData();
        if (activeTab === 'leaderboard') fetchLeaderboard();
      } catch (err) {
        Swal.fire('Error', 'Gagal approve task', 'error');
      }
    }
  };

  const openAddModal = (picId = '') => {
    setEditTask(null);
    setFormData({
      title: '',
      category: '',
      points: 0,
      deadline: '',
      pic_id: picId ? String(picId) : ''
    });
    setShowModal(true);
  };

  const openEditModal = (task) => {
    setEditTask(task);
    setFormData({
      title: task.title,
      category: task.category,
      points: task.points,
      deadline: task.deadline ? new Date(task.deadline).toISOString().split('T')[0] : '',
      pic_id: task.pic_id
    });
    setShowModal(true);
  };

  const handleReject = async (id) => {
    const { value: feedback } = await Swal.fire({
      title: 'Tolak Task',
      input: 'textarea',
      inputLabel: 'Alasan Penolakan / Revisi',
      inputPlaceholder: 'Tuliskan apa yang perlu diperbaiki...',
      showCancelButton: true,
      inputValidator: (value) => {
        if (!value) {
          return 'Alasan tidak boleh kosong!'
        }
      }
    });

    if (feedback) {
      try {
        const token = localStorage.getItem('token');
        await axios.put(`${API_URL}/api/tasks/${id}/reject`, { feedback }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        Swal.fire('Ditolak!', 'Task dikembalikan untuk direvisi.', 'success');
        fetchData();
      } catch (err) {
        Swal.fire('Error', 'Gagal menolak task', 'error');
      }
    }
  };

  const UPLOADS_BASE = (API_URL || "").replace(/\/api\/?$/, "") || API_URL;

  const formatDate = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusVariant = (status) => {
    if (status === 'claimed') return 'warning';
    if (status === 'approved') return 'success';
    if (status === 'rejected') return 'danger';
    return 'secondary';
  };

  const getStatusBorderColor = (status) => {
    if (status === 'claimed') return '#ffc107';
    if (status === 'approved') return '#198754';
    if (status === 'rejected') return '#dc3545';
    return '#6c757d';
  };

  const countTasksByStatus = (taskList, status) =>
    taskList.filter(task => task.status === status).length;

  const filterOverviewTasks = (taskList) => {
    const filtered = overviewFilter === 'active'
      ? taskList.filter(task => task.status !== 'approved')
      : taskList;

    const statusOrder = { pending: 0, claimed: 1, rejected: 2, approved: 3 };
    return [...filtered].sort((a, b) => {
      const statusDiff = (statusOrder[a.status] ?? 99) - (statusOrder[b.status] ?? 99);
      if (statusDiff !== 0) return statusDiff;
      return new Date(a.deadline) - new Date(b.deadline);
    });
  };

  const tasksByOfficer = officers.map(officer => {
    const officerTasks = tasks.filter(task => String(task.pic_id) === String(officer.id));
    return {
      officer,
      tasks: officerTasks,
      visibleTasks: filterOverviewTasks(officerTasks)
    };
  });

  const renderTaskCardActions = (task) => (
    <div className="d-flex flex-wrap gap-1 mt-2">
      {task.status === 'claimed' && (
        <>
          <Button variant="success" size="sm" onClick={() => handleApprove(task.id)}>Approve</Button>
          <Button variant="outline-danger" size="sm" onClick={() => handleReject(task.id)}>Tolak</Button>
        </>
      )}
      {task.status !== 'approved' && (
        <Button variant="outline-warning" size="sm" onClick={() => openEditModal(task)}>Edit</Button>
      )}
      <Button variant="outline-danger" size="sm" onClick={() => handleDelete(task.id)}>Delete</Button>
    </div>
  );

  const columns = [
    { name: 'Title', selector: row => row.title, sortable: true },
    { name: 'PIC', selector: row => row.pic_name, sortable: true },
    { name: 'Category', selector: row => row.category, sortable: true },
    { name: 'Points', selector: row => row.points, sortable: true },
    { name: 'Deadline', selector: row => new Date(row.deadline).toLocaleDateString('id-ID'), sortable: true },
    {
      name: 'Status',
      cell: row => {
        return <Badge bg={getStatusVariant(row.status)}>{row.status.toUpperCase()}</Badge>;
      }
    },
    {
        name: 'Evidence',
        cell: row => {
            if (row.status === 'pending') return '-';
            return (
                <div style={{fontSize: '12px'}}>
                    {row.evidence_text && <div><b>Teks/Link:</b> {row.evidence_text}</div>}
                    {row.evidence_file && <div><a href={`${UPLOADS_BASE}/uploads/${row.evidence_file}`} target="_blank" rel="noopener noreferrer">View File</a></div>}
                </div>
            );
        }
    },
    {
      name: 'Actions',
      cell: row => (
        <div className="flex flex-col gap-1 p-1">
          {row.status === 'claimed' && (
            <>
                <Button variant="success" size="sm" onClick={() => handleApprove(row.id)}>Approve</Button>
                <Button variant="outline-danger" size="sm" onClick={() => handleReject(row.id)}>Tolak</Button>
            </>
          )}
          {row.status !== 'approved' && (
              <Button variant="warning" size="sm" onClick={() => openEditModal(row)}>Edit</Button>
          )}
          <Button variant="danger" size="sm" onClick={() => handleDelete(row.id)}>Delete</Button>
        </div>
      )
    }
  ];

  const filteredTasks = tasks.filter(task => {
    if (filterStatus && task.status !== filterStatus) return false;
    if (filterPic && String(task.pic_id) !== String(filterPic)) return false;
    return true;
  });

  const [lbYear, lbMonth] = leaderboardMonth.split('-');
  const leaderboardPeriodLabel = `${MONTH_NAMES[parseInt(lbMonth, 10) - 1]} ${lbYear}`;

  const leaderboardColumns = [
    {
      name: 'Rank',
      width: '80px',
      cell: row => {
        if (row.rank === 1 && row.total_points > 0) return <span title="Best Employee">🥇 #{row.rank}</span>;
        if (row.rank === 2 && row.total_points > 0) return <span>🥈 #{row.rank}</span>;
        if (row.rank === 3 && row.total_points > 0) return <span>🥉 #{row.rank}</span>;
        return `#${row.rank}`;
      }
    },
    { name: 'Nama Officer', selector: row => row.full_name, sortable: true },
    { name: 'Posisi', selector: row => row.position || '-', sortable: true },
    {
      name: 'Total Poin',
      selector: row => row.total_points,
      sortable: true,
      cell: row => <strong>{row.total_points}</strong>
    },
    { name: 'Task Selesai', selector: row => row.tasks_completed, sortable: true }
  ];

  const ExpandedComponent = ({ data }) => (
    <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #ddd' }}>
      <p style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        <strong>Title Lengkap:</strong><br/>
        {data.title}
      </p>
      {data.status === 'rejected' && data.admin_feedback && (
        <div className="mt-2 text-danger">
          <strong>Alasan Penolakan/Revisi:</strong><br/>
          <span style={{ whiteSpace: 'pre-wrap' }}>{data.admin_feedback}</span>
        </div>
      )}
    </div>
  );

  return (
    <DashboardLayout role="superadmin">
      <div className="p-4">
        <h2 className="mb-4 text-2xl font-bold">Task Management</h2>

        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'tasks')}>
          <Nav variant="tabs" className="mb-4">
            <Nav.Item>
              <Nav.Link eventKey="tasks">Daftar Task</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="leaderboard">Leaderboard Bulanan</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="overview">Helicopter View</Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            <Tab.Pane eventKey="tasks">
              <Button variant="primary" className="mb-4" onClick={() => openAddModal()}>Add Task</Button>

              <div className="row mb-4 g-3">
                <div className="col-md-4">
                  <Form.Label>Filter Status</Form.Label>
                  <Form.Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                    <option value="">Semua Status</option>
                    <option value="pending">Pending</option>
                    <option value="claimed">Claimed</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </Form.Select>
                </div>
                <div className="col-md-4">
                  <Form.Label>Filter PIC</Form.Label>
                  <Form.Select value={filterPic} onChange={(e) => setFilterPic(e.target.value)}>
                    <option value="">Semua PIC</option>
                    {officers.map(off => (
                      <option key={off.id} value={off.id}>{off.full_name}</option>
                    ))}
                  </Form.Select>
                </div>
              </div>
              
              <DataTable
                columns={columns}
                data={filteredTasks}
                pagination
                highlightOnHover
                responsive
                expandableRows
                expandOnRowClicked
                expandableRowsComponent={ExpandedComponent}
              />
            </Tab.Pane>

            <Tab.Pane eventKey="leaderboard">
              <div className="row mb-4 g-3 align-items-end">
                <div className="col-md-4">
                  <Form.Label>Pilih Bulan</Form.Label>
                  <Form.Control
                    type="month"
                    value={leaderboardMonth}
                    onChange={(e) => setLeaderboardMonth(e.target.value)}
                  />
                </div>
              </div>

              {topPerformer && (
                <div className="card mb-4 border-warning shadow-sm">
                  <div className="card-body d-flex flex-wrap align-items-center gap-3">
                    <div style={{ fontSize: '2.5rem' }}>🏆</div>
                    <div>
                      <p className="text-muted mb-0">Best Employee — {leaderboardPeriodLabel}</p>
                      <h4 className="mb-1 fw-bold">{topPerformer.full_name}</h4>
                      <p className="mb-0 text-muted">
                        {topPerformer.position || 'Officer'} · {topPerformer.total_points} poin · {topPerformer.tasks_completed} task selesai
                      </p>
                    </div>
                    <Badge bg="warning" text="dark" className="ms-auto">Employee of the Month</Badge>
                  </div>
                </div>
              )}

              {!leaderboardLoading && !topPerformer && (
                <div className="alert alert-info mb-4">
                  Belum ada task yang disetujui pada {leaderboardPeriodLabel}.
                </div>
              )}

              <DataTable
                columns={leaderboardColumns}
                data={leaderboard}
                progressPending={leaderboardLoading}
                pagination
                highlightOnHover
                responsive
                conditionalRowStyles={[
                  {
                    when: row => row.rank === 1 && row.total_points > 0,
                    style: { backgroundColor: 'rgba(255, 193, 7, 0.15)' }
                  }
                ]}
              />
            </Tab.Pane>

            <Tab.Pane eventKey="overview">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
                <div>
                  <h5 className="mb-1 fw-bold">Mapping Task per Officer</h5>
                  <p className="text-muted mb-0 small">
                    Pantau beban task setiap anggota tim dan tambah task langsung ke PIC yang dipilih.
                  </p>
                </div>
                <div style={{ minWidth: '220px' }}>
                  <Form.Label className="small mb-1">Tampilan Task</Form.Label>
                  <Form.Select
                    value={overviewFilter}
                    onChange={(e) => setOverviewFilter(e.target.value)}
                  >
                    <option value="active">Aktif (tanpa approved)</option>
                    <option value="all">Semua Task</option>
                  </Form.Select>
                </div>
              </div>

              {officers.length === 0 ? (
                <div className="alert alert-info mb-0">Belum ada data officer.</div>
              ) : (
                <div
                  className="d-flex gap-3 overflow-auto pb-2"
                  style={{ minHeight: '520px', alignItems: 'stretch' }}
                >
                  {tasksByOfficer.map(({ officer, tasks: officerTasks, visibleTasks }) => (
                    <div
                      key={officer.id}
                      className="flex-shrink-0"
                      style={{ width: '300px' }}
                    >
                      <div className="card h-100 shadow-sm border-0">
                        <div className="card-header bg-white border-bottom">
                          <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                            <div>
                              <h6 className="mb-0 fw-bold">{officer.full_name}</h6>
                              <small className="text-muted">{officer.position || 'Officer'}</small>
                            </div>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => openAddModal(officer.id)}
                            >
                              + Task
                            </Button>
                          </div>
                          <div className="d-flex flex-wrap gap-1">
                            <Badge bg="secondary">{countTasksByStatus(officerTasks, 'pending')} pending</Badge>
                            <Badge bg="warning" text="dark">{countTasksByStatus(officerTasks, 'claimed')} claimed</Badge>
                            <Badge bg="danger">{countTasksByStatus(officerTasks, 'rejected')} rejected</Badge>
                            <Badge bg="success">{countTasksByStatus(officerTasks, 'approved')} approved</Badge>
                          </div>
                        </div>

                        <div
                          className="card-body bg-light"
                          style={{ maxHeight: '620px', overflowY: 'auto' }}
                        >
                          {visibleTasks.length === 0 ? (
                            <div className="text-center text-muted small py-4">
                              {overviewFilter === 'active'
                                ? 'Tidak ada task aktif untuk officer ini.'
                                : 'Belum ada task untuk officer ini.'}
                            </div>
                          ) : (
                            visibleTasks.map(task => (
                              <div
                                key={task.id}
                                className="card mb-3 shadow-sm"
                                style={{ borderLeft: `4px solid ${getStatusBorderColor(task.status)}` }}
                              >
                                <div className="card-body p-3">
                                  <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                                    <strong className="small">{task.title}</strong>
                                    <Badge bg={getStatusVariant(task.status)}>
                                      {task.status}
                                    </Badge>
                                  </div>
                                  <div className="small text-muted">
                                    <div><strong>Kategori:</strong> {task.category}</div>
                                    <div><strong>Poin:</strong> {task.points}</div>
                                    <div><strong>Deadline:</strong> {formatDate(task.deadline)}</div>
                                    {task.status === 'approved' && (
                                      <div><strong>Poin didapat:</strong> {task.earned_points ?? 0}</div>
                                    )}
                                  </div>

                                  {task.status !== 'pending' && (task.evidence_text || task.evidence_file) && (
                                    <div className="small mt-2 p-2 bg-light rounded border">
                                      {task.evidence_text && (
                                        <div><strong>Bukti:</strong> {task.evidence_text}</div>
                                      )}
                                      {task.evidence_file && (
                                        <div>
                                          <a
                                            href={`${UPLOADS_BASE}/uploads/${task.evidence_file}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            Lihat file bukti
                                          </a>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {task.status === 'rejected' && task.admin_feedback && (
                                    <div className="small mt-2 text-danger">
                                      <strong>Feedback:</strong> {task.admin_feedback}
                                    </div>
                                  )}

                                  {renderTaskCardActions(task)}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>

        <Modal show={showModal} onHide={() => setShowModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>{editTask ? 'Edit Task' : 'Add Task'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control type="text" name="title" value={formData.title} onChange={handleChange} required />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Category</Form.Label>
                <Form.Select name="category" value={formData.category} onChange={handleChange} required>
                  <option value="">-- Select Category --</option>
                  <option value="Runtera">Runtera</option>
                  <option value="Shaff Konveksi">Shaff Konveksi</option>
                  <option value="Kursus Coding Online">Kursus Coding Online</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Points</Form.Label>
                <Form.Control type="number" name="points" value={formData.points} onChange={handleChange} required />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Deadline</Form.Label>
                <Form.Control type="date" name="deadline" value={formData.deadline} onChange={handleChange} required />
              </Form.Group>
              <Form.Group className="mb-4">
                <Form.Label>PIC (Officer)</Form.Label>
                <Form.Select name="pic_id" value={formData.pic_id} onChange={handleChange} required>
                  <option value="">-- Select Officer --</option>
                  {officers.map(off => (
                    <option key={off.id} value={off.id}>{off.full_name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <div className="d-flex justify-content-end">
                <Button variant="secondary" className="me-2" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button variant="primary" type="submit">{editTask ? 'Update' : 'Save'}</Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      </div>
    </DashboardLayout>
  );
};

export default TaskManagement;
